import styled from '@emotion/styled'
import {
  colors,
  fontSize,
  fontWeight,
  spacing,
  Tooltip,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useMemo } from 'react'
import moment from 'moment'
import { type Styles } from 'react-modal'
import {
  getIconByType,
  getTitleByType,
} from 'domains/shifts/components/components/staffing-alert/StaffingAlertCard'
import { intervalDifference } from 'domains/shifts/components/utils/intervalUtils'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { type RecommendationTypeEnum } from 'domains/shifts/api'
import { type ShopperWithShifts } from 'domains/shifts/components/types'
import { isShopperAvailable } from 'domains/shifts/components/utils/shiftsUtils'
import { RecommendedShiftToolTip } from 'domains/shifts/components/components/recommended-shifts/RecommendedShiftToolTip'
import { type PartnerPickRecurringShiftsFormData } from 'domains/shifts/components/recurring-shifts/recurringShift.types'
import { getTimeOption } from 'domains/shifts/components/recurring-shifts/partnerPickRecurringShiftForm.utils'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { trackEvent } from 'utils/events/trackEvent'

const TimeSpan = styled.div({
  display: 'block',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  lineHeight: fontSize.X18,
})

interface Props {
  type: RecommendationTypeEnum
  inactive?: boolean
  start: Date
  end: Date
  regularStoreOpen: Date
  shoppers: ShopperWithShifts[]
  formData: PartnerPickRecurringShiftsFormData
  zoneOffset: number
}

type TooltipWrapperStyle = {
  startColumn: number
  endColumn: number
}

const TooltipWrapper = styled.div<TooltipWrapperStyle>(({ startColumn, endColumn }) => ({
  position: 'relative',
  gridColumn: `${startColumn} / ${endColumn}`,
  overflow: 'hidden',
  gridRow: '1',
}))

type ContainerStyle = {
  type: RecommendationTypeEnum
  inactive?: boolean
}
const cellColor = (inactive: boolean) =>
  inactive ? colors.GRAYSCALE.X50 : colors.DETRIMENTAL.REGULAR

const Container = styled.div<ContainerStyle>(({ inactive = false }) => ({
  margin: '17px 0',
  border: `1px dashed ${cellColor(inactive)}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: spacing.X32,
  borderRadius: '6px',
  fontSize: fontSize.X12,
  fontWeight: fontWeight.SEMIBOLD,
  color: cellColor(inactive),
  backgroundColor: inactive ? colors.GRAYSCALE.X20 : colors.DETRIMENTAL.LIGHT,
  '&:hover': {
    cursor: inactive ? 'default' : 'pointer',
  },
}))
interface Props {
  type: RecommendationTypeEnum
  inactive?: boolean
  start: Date
  end: Date
  regularStoreOpen: Date
  shoppers: ShopperWithShifts[]
  recommendationId: string
  warehouseId: string
  warehouseLocationId: string
  userAccountId: string
}

export const RecommendedShiftCell: FunctionComponent<Props> = ({
  type,
  inactive,
  regularStoreOpen,
  start,
  end,
  shoppers,
  formData,
  zoneOffset,
  recommendationId,
  warehouseLocationId,
  warehouseId,
  userAccountId,
}) => {
  const componentRef = React.createRef<HTMLDivElement>()
  const firstGridColumn = intervalDifference(regularStoreOpen, start) + 1
  const lastGridColumn = intervalDifference(regularStoreOpen, end) + 1

  const messages = useDomainMessages({
    demandSurge: 'shiftsDomain.staffingAlert.demandSurge',
    noRunner: 'shiftsDomain.staffingAlert.noRunner',
  })

  const { setShiftsState, openShiftsModal } = useShiftsStateContext()

  const TIME_FORMAT = 'ha'

  const availableShoppers = useMemo(() => {
    const recommendedShiftInterval = {
      start: new Date(start).getTime(),
      end: new Date(end).getTime(),
    }
    return shoppers.filter(shopper => {
      const shifts = shopper.shifts.map(shift => ({
        start: new Date(shift.starts_at).getTime(),
        end: new Date(shift.ends_at).getTime(),
      }))

      return isShopperAvailable(shifts, recommendedShiftInterval)
    })
  }, [shoppers, start, end])

  const formattedStart = moment.utc(start).add(zoneOffset, 'h').format(TIME_FORMAT)
  const formattedEnd = moment.utc(end).add(zoneOffset, 'h').format(TIME_FORMAT)
  const timeString = `${formattedStart}-${formattedEnd}`
  const cellTitle = inactive ? getTitleByType(type, messages) : timeString

  const getModalStyles = () => {
    const SHIFTS_MODAL_WIDTH = 345
    const { bottom, left, height } = componentRef.current.getBoundingClientRect()
    const newBottom = Math.floor(window.innerHeight - bottom + height - 5)
    let newLeft = left
    if (left + SHIFTS_MODAL_WIDTH >= window.innerWidth) {
      newLeft = window.innerWidth - SHIFTS_MODAL_WIDTH
    }
    return {
      content: {
        bottom: newBottom,
        left: newLeft,
        position: 'absolute',
      },
    } as Styles
  }

  const handleCellOnClick = () => {
    if (inactive) return

    trackEvent({
      id: 'ent.shifts.recommendations.shift_cell.clicked',
      description: 'User clicked on the recommendation shift cell on the shifts table',
      data: {
        recommendationId,
        recommendationType: type,
        numberOfAvailableDevices: shoppers.length,
        startTime: formattedStart,
        endTime: formattedEnd,
        warehouseLocationId,
        warehouseId,
        userAccountId,
      },
    })

    setShiftsState(prevState => ({
      ...prevState,
      selectedRecommendationShiftRange: { start: firstGridColumn, end: lastGridColumn },
    }))
    openShiftsModal({
      isRecommendationMode: true,
      recommendationType: type,
      isEditMode: false,
      shopperId: null,
      formData: {
        ...formData,
        shoppers: availableShoppers,
        from: getTimeOption(start, zoneOffset),
        to: getTimeOption(end, zoneOffset),
        daysOfWeek: [],
      },
      modalStyles: getModalStyles(),
    })
  }
  return (
    <TooltipWrapper startColumn={firstGridColumn} endColumn={lastGridColumn}>
      <Tooltip
        target={
          <Container type={type} inactive={inactive} onClick={handleCellOnClick} ref={componentRef}>
            {getIconByType(type, inactive, colors.DETRIMENTAL.REGULAR, colors.GRAYSCALE.X50)}
            <TimeSpan data-testid="recommendation-shift-cell-label">{cellTitle}</TimeSpan>
          </Container>
        }
        placement="top"
      >
        <RecommendedShiftToolTip
          timeString={timeString}
          shiftDetails={[getTitleByType(type, messages)]}
        />
      </Tooltip>
    </TooltipWrapper>
  )
}
